import { LuAlertCircle, LuCalendar } from "react-icons/lu";
import { translate } from "../../../../utils/Common";
import LockersSearchList from "../../../../components/LockersSearchList";
import { usePlatforms } from "../../../../hooks/usePlatforms";
import { useForm } from "react-hook-form";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import TypeIncidentSelect from "../TypeIncidentSelect";
import PriorityIncidentList from "../PriorityIncidentList";
import { useIncidents } from "../../../../hooks/useIncidents";
import ModalContext from "../../../../contexts/modal";
import { useHistory, useLocation } from "react-router-dom";
import { FaChevronLeft, FaDoorOpen } from "react-icons/fa";
import { IoSettingsOutline } from "react-icons/io5";
import { PiLockers, PiPackageFill } from "react-icons/pi";
import { TbFileDescription, TbFlag3 } from "react-icons/tb";
import AttachUploadsFiles from "../../../../components/ui/AttachUploadsFiles";
import OriginIncidentList from "../OriginIncidentList";
import ClientsSearchList from "../../../../components/ClientsSearchList";
import AuthContext from "../../../../contexts/auth";
import { verifyAccessViewPermissions } from "../../../../config/permissions";

import {
  PageContainer,
  PageHeader,
  ContentCard,
} from "../../../../components/layout";
import Button from "../../../../components/common/Button";
import { ToastContainer, useToast } from "../../../../components/common/Toast";

import styles from "./RegisterIncident.module.css";

const RegisterIncdent = () => {
  const {
    register,
    errors,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    setValue,
    getValues,
  } = useForm();

  const { registerIncident, getPresignedUploadUrl } = useIncidents();
  const modalContext = useContext(ModalContext);
  const history = useHistory();
  const location = useLocation();

  const [isSendingData, setIsSendingData] = useState(false);
  const [isTypeIncidentSoftware, setIsTypeIncidentSoftware] = useState(false);
  const [isBoxit, setIsBoxit] = useState(false);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPlataforma, setSelectedPlataforma] = useState(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [attachmentsError, setAttachmentsError] = useState("");
  const { toasts, showError, showSuccess, removeToast } = useToast();
  const userContext = useContext(AuthContext);
  const { platforms } = usePlatforms();

  // Registrar campos controlados manualmente para que watch/getValues funcionen
  useEffect(() => {
    register({ name: "plataforma" });
    register({ name: "puerta" });
    register({ name: "idPackage" });
  }, [register]);

  const idPlatform = useRef({});
  idPlatform.current = watch("plataforma", "");
  const handleIsBoxit = useCallback((value) => setIsBoxit(value), []);

  const dateInputRef = useRef(null);

  const resetForm = () => {
    reset();
    setFiles([]);
    setInputDateToday();
    setSelectedClient(null);
    setSelectedPlataforma(null);
    setFormResetKey((k) => k + 1);
    setIsTypeIncidentSoftware(false);
  };

  const setInputDateToday = () => {
    const today = new Date().toISOString().split("T")[0];
    if (dateInputRef.current) {
      dateInputRef.current.value = today;
    }
  };

  const canCreate = verifyAccessViewPermissions(["create"], { path: location.pathname });
  const canUpload = verifyAccessViewPermissions(["upload"], { path: location.pathname });

  const submitIncident = async (data) => {
    try {
      if (!canCreate) return;
      setIsSendingData(true);
      setIsLoading(true);
      const controller = new AbortController();

      const plataformaVal = data.plataforma ?? getValues("plataforma");
      const puertaVal = data.puerta ?? getValues("puerta");

      const payload = {
        typeIncident: data.typeIncident,
        priorityIncident: data.priorityIncident,
        description: data.description,
        dateIncident: data.dateIncident ?? new Date().toISOString().split("T")[0],
        plataforma: plataformaVal !== undefined && plataformaVal !== "" ? plataformaVal : 0,
        puerta: puertaVal !== undefined ? puertaVal : "",
        idPackage: data.idPackage !== undefined ? data.idPackage : "",
        clientAsigned: selectedClient,
        originIncident: data.originIncident,
        imageUrls: files.map((f) => f.fileUrl).filter(Boolean),
      };

      const result = await registerIncident(payload, controller.signal);
      setIsLoading(false);
      if (result) {
        if (result.code === 200) {
          if (result.payload) {
            modalContext.confirm(
              translate(
                "dashboard.packages.loadpackage.formProvider.successTittle"
              ),
              <div>
                {translate("global.incident")} #{" "}
                <strong>{` ${result.payload.payload.incidentId}`}</strong>{" "}
                {translate("global.successrRegisterIncident")}
              </div>,
              <Button
                variant="primary"
                size="sm"
                onClick={handleCloseModal}
              >
                {translate("global.accept")}
              </Button>,
              true
            );
            resetForm();
          }
        } else {
          modalContext.confirm(
            translate("global.alerts.ops"),
            translate("global.errorRegisterIncident"),
            <Button
              variant="outline"
              size="sm"
              onClick={() => modalContext.setIsOpenModal(false)}
            >
              {translate("global.accept")}
            </Button>,
            true
          );
          reset();
        }
      }
    } catch (err) {
      console.log(err);
      modalContext.confirm(
        translate("global.alerts.ops"),
        translate(
          "dashboard.packages.loadpackages.formProvider.errorRegisterPackage"
        ),
        <Button
          variant="outline"
          size="sm"
          onClick={() => modalContext.setIsOpenModal(false)}
        >
          {translate("global.accept")}
        </Button>,
        true
      );
    } finally {
      setIsSendingData(false);
    }
  };

  const handleCloseModal = () => {
    modalContext.setIsOpenModal(false);
    history.push("/dashboard/incidents");
  };

  const handleBack = () => history.push("/dashboard/incidents");

  useEffect(() => {
    const result =
      watch("typeIncident") !== "9" &&
      watch("typeIncident") !== translate("global.select");
    setIsTypeIncidentSoftware(result);
  }, [watch]);

  useEffect(() => {
    setInputDateToday();
  }, []);

  const plataformaValue = watch("plataforma");
  useEffect(() => {
    if (plataformaValue && platforms?.length) {
      const platform = platforms.find((p) => p.boxitLocationId === Number(plataformaValue));
      handleIsBoxit(!!platform?.IsBoxit);
    } else {
      handleIsBoxit(false);
    }
  }, [plataformaValue, platforms]);

  const canEditClient = userContext?.user?.roleId >= 5 || userContext?.user?.RoleBli >= 5;

  const onUploadToS3 = useCallback(
    async (file) => {
      if (!canUpload) throw new Error("NOT_AUTHORIZED");
      const { uploadUrl, fileUrl } = await getPresignedUploadUrl(file.name, file.type);
      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putResponse.ok) throw new Error("UPLOAD_FAILED");
      return { fileUrl };
    },
    [getPresignedUploadUrl, canUpload]
  );

  return (
    <PageContainer>
      <PageHeader
        title={translate("global.registerIncident")}
        breadcrumbs={[
          <span
            key="incidents"
            onClick={handleBack}
            className={styles.breadcrumbLink}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBack();
              }
            }}
          >
            {translate("global.listIncident")}
          </span>,
          translate("global.registerIncident"),
        ]}
        icon={<LuAlertCircle size={28} />}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="outline"
              size="md"
              leftIcon={<FaChevronLeft size={18} />}
              onClick={handleBack}
            >
              {translate("global.return")}
            </Button>
          </div>
        }
        divider={false}
      />

      {!canCreate ? (
        <ContentCard padding="md">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(107,114,128,0.12)",
                color: "#374151",
                flex: "0 0 auto",
              }}
              aria-hidden
            >
              <LuAlertCircle size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
                {translate("dashboard.configuration.noPermissionTitle")}
              </div>
              <div style={{ marginTop: 4, color: "#6B7280" }}>
                {translate("dashboard.clients.formSearch.NOT_ACCESS")}
              </div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="primary" type="button" onClick={handleBack}>
                  {translate("global.goBack")}
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>
      ) : (
      <ContentCard padding="md">
        <form onSubmit={handleSubmit(submitIncident)}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="typeIncident" className={styles.formLabel}>
                <IoSettingsOutline size={16} />
                {translate("global.typeIncident")}{" "}
                <span className={styles.required}>*</span>
              </label>
              <TypeIncidentSelect
                key={`type-${formResetKey}`}
                errors={errors}
                register={register}
                setValue={setValue}
                nameShow={translate("global.select")}
                readOnly={false}
                sizeInput={"form-control-md"}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="priorityIncident" className={styles.formLabel}>
                <TbFlag3 size={16} />
                {translate("global.priority")}{" "}
                <span className={styles.required}>*</span>
              </label>
              <PriorityIncidentList
                errors={errors}
                register={register}
                setValue={setValue}
                nameShow={translate("global.select")}
                readOnly={false}
                idPlatform={idPlatform.current}
                sizeInput={"form-control-md"}
              />
            </div>

            {canEditClient && (
              <>
                <div className={styles.formGroup}>
                  <label htmlFor="originIncident" className={styles.formLabel}>
                    {translate("global.origin")}{" "}
                    <span className={styles.required}>*</span>
                  </label>
                  <OriginIncidentList
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    nameShow={translate("global.select")}
                    readOnly={false}
                    sizeInput={"form-control-md"}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="incidentClient" className={styles.formLabel}>
                    {translate("global.client")}{" "}
                    <span className={styles.formLabelOptional}>
                      ({translate("global.optional")})
                    </span>
                  </label>
                  <ClientsSearchList
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    register={register}
                    allClients
                    setSelectOption={setSelectedClient}
                  />
                </div>
              </>
            )}
          </div>

          {isTypeIncidentSoftware && (
            <div className={styles.formSection}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <PiLockers size={16} />
                    {translate("global.locker")}
                  </label>
                  <LockersSearchList
                    key={`locker-${formResetKey}`}
                    errors={errors}
                    fieldName="plataforma"
                    name="plataforma"
                    value={plataformaValue}
                    placeholder={translate("global.selectLocker")}
                    isRequired={false}
                    setSelectOption={(id) => {
                      setSelectedPlataforma(id ?? null);
                      setValue("plataforma", id ?? "", { shouldValidate: true });
                    }}
                  />
                </div>

                {isBoxit &&
                  idPlatform.current !== translate("global.select") &&
                  idPlatform.current !== "" && (
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        <FaDoorOpen size={16} />
                        {translate("dashboard.platforms.numDoor")}
                      </label>
                      <input
                        type="text"
                        className={styles.formInput}
                        name="puerta"
                        ref={register()}
                      />
                    </div>
                  )}
              </div>
            </div>
          )}

          {isTypeIncidentSoftware && (
            <div className={styles.formSection}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <PiPackageFill size={16} />
                    {translate("global.packageId")}
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    name="idPackage"
                    placeholder={translate("global.packageId.placeholder")}
                    ref={register()}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={styles.formSection}>
            <div className={styles.descriptionGrid}>
              <div className={styles.formGroup}>
                <label
                  htmlFor="incidentDescription"
                  className={styles.formLabel}
                >
                  <TbFileDescription size={16} />
                  {translate("global.description")}{" "}
                  <span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.formInput}
                  id="incidentDescription"
                  rows={8}
                  name="description"
                  required
                  ref={register()}
                  style={{ resize: "vertical", minHeight: 180 }}
                />
              </div>
              {canUpload && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {translate("global.attachfiles")}
                  </label>
                  <span className={styles.attachmentsInfo}>
                    {translate("global.attachfiles.helper")}
                  </span>
                  {attachmentsError && (
                    <div className={styles.attachmentsError} role="alert">
                      {attachmentsError}
                    </div>
                  )}
                  <AttachUploadsFiles
                    files={files}
                    setFiles={setFiles}
                    onUploadToS3={onUploadToS3}
                    onErrorChange={setAttachmentsError}
                    onShowErrorToast={(msg) => showError(msg)}
                    onShowSuccessToast={(msg) => showSuccess(msg)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.dateSection}>
            <label htmlFor="dateIncident" className={styles.formLabel}>
              <LuCalendar size={16} />
              {translate("global.dateIncident")}{" "}
              <span className={styles.required}>*</span>
            </label>
            <input
              type="date"
              id="dateIncident"
              className={`${styles.formInput} ${styles.dateInput}`}
              required
              ref={(e) => {
                register(e);
                dateInputRef.current = e;
              }}
              name="dateIncident"
            />
          </div>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={resetForm}
            >
              {translate("global.clean")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={isSendingData}
              disabled={isSendingData || files.some((f) => f.status === "uploading")}
            >
              {translate("global.create")}
            </Button>
          </div>
        </form>
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </ContentCard>
      )}
    </PageContainer>
  );
};

export default RegisterIncdent;
